// EnergyMarketplaceList.js
import React, { useMemo, useState } from "react";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    Tooltip,
    XAxis,
    YAxis,
    BarChart,
    Bar,
    CartesianGrid,
} from "recharts";
import {
    Search,
    Filter,
    ChevronDown,
    ChevronUp,
    MapPin,
    Factory,
    BatteryCharging,
    Star,
    Zap,
    CalendarDays,
    Layers,
    Gauge,
    Eye,
    X,
    Menu,
    User,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import PageFooter from "../components/PageFooter";

/**
 * Requirements:
 *   npm i react recharts lucide-react
 *   Tailwind CSS styles assumed
 *
 * What’s new:
 * - Sticky Header with brand, nav menu, search shortcut and Login button
 * - Mobile menu
 * - Footer with helpful links
 * - Everything else from the marketplace layout (filters, card grid, detail drawer)
 */

// ---------- Mock data ----------
const companies = [
    {
        id: "alpha",
        name: "Alpha Energy",
        type: "Producer",
        region: "TR",
        fuel: "Solar",
        rating: 4.6,
        pricePerKWh: 0.081,
        capacityMW: 220,
        utilizationPct: 78,
        currentProductionMWh: 1240,
        previousProductionMWh: 1175,
        plannedProductionMWh: 1320,
        weeklyTrend: [
            { w: "W-6", mwh: 165 }, { w: "W-5", mwh: 170 }, { w: "W-4", mwh: 180 },
            { w: "W-3", mwh: 190 }, { w: "W-2", mwh: 175 }, { w: "W-1", mwh: 185 }, { w: "Now", mwh: 190 },
        ],
        plants: [
            { name: "Alpha Solar Park A", mw: 120, location: "Ankara" },
            { name: "Alpha Solar Park B", mw: 100, location: "Konya" },
        ],
        monthPlan: [
            { month: "Sep", planned: 1320 }, { month: "Oct", planned: 1290 }, { month: "Nov", planned: 1210 }, { month: "Dec", planned: 1180 },
        ],
        lastUpdated: "2025-09-08",
    },
    {
        id: "beta",
        name: "Beta Power",
        type: "Producer",
        region: "EU",
        fuel: "Wind",
        rating: 4.3,
        pricePerKWh: 0.079,
        capacityMW: 180,
        utilizationPct: 71,
        currentProductionMWh: 980,
        previousProductionMWh: 940,
        plannedProductionMWh: 1040,
        weeklyTrend: [
            { w: "W-6", mwh: 130 }, { w: "W-5", mwh: 135 }, { w: "W-4", mwh: 142 },
            { w: "W-3", mwh: 150 }, { w: "W-2", mwh: 148 }, { w: "W-1", mwh: 155 }, { w: "Now", mwh: 160 },
        ],
        plants: [
            { name: "Beta Wind Farm North", mw: 95, location: "Gdansk" },
            { name: "Beta Wind Farm East", mw: 85, location: "Riga" },
        ],
        monthPlan: [
            { month: "Sep", planned: 1040 }, { month: "Oct", planned: 1000 }, { month: "Nov", planned: 980 }, { month: "Dec", planned: 960 },
        ],
        lastUpdated: "2025-09-07",
    },
    {
        id: "hydro",
        name: "HydroStream Power",
        type: "Producer",
        region: "EU",
        fuel: "Hydro",
        rating: 4.8,
        pricePerKWh: 0.074,
        capacityMW: 260,
        utilizationPct: 83,
        currentProductionMWh: 1125,
        previousProductionMWh: 1090,
        plannedProductionMWh: 1180,
        weeklyTrend: [
            { w: "W-6", mwh: 150 }, { w: "W-5", mwh: 155 }, { w: "W-4", mwh: 160 },
            { w: "W-3", mwh: 165 }, { w: "W-2", mwh: 163 }, { w: "W-1", mwh: 170 }, { w: "Now", mwh: 175 },
        ],
        plants: [{ name: "HS Dam Complex", mw: 260, location: "Bucharest" }],
        monthPlan: [
            { month: "Sep", planned: 1180 }, { month: "Oct", planned: 1200 }, { month: "Nov", planned: 1150 }, { month: "Dec", planned: 1130 },
        ],
        lastUpdated: "2025-09-09",
    },
    {
        id: "wind",
        name: "WindForce Tech",
        type: "Producer",
        region: "TR",
        fuel: "Wind",
        rating: 4.1,
        pricePerKWh: 0.076,
        capacityMW: 150,
        utilizationPct: 66,
        currentProductionMWh: 860,
        previousProductionMWh: 820,
        plannedProductionMWh: 900,
        weeklyTrend: [
            { w: "W-6", mwh: 110 }, { w: "W-5", mwh: 112 }, { w: "W-4", mwh: 118 },
            { w: "W-3", mwh: 122 }, { w: "W-2", mwh: 120 }, { w: "W-1", mwh: 124 }, { w: "Now", mwh: 130 },
        ],
        plants: [{ name: "WF Ridge Array", mw: 150, location: "Çanakkale" }],
        monthPlan: [
            { month: "Sep", planned: 900 }, { month: "Oct", planned: 870 }, { month: "Nov", planned: 840 }, { month: "Dec", planned: 810 },
        ],
        lastUpdated: "2025-09-06",
    },
];

// // ---------- Header ----------
// function Header({ onLogin }) {
//     const [open, setOpen] = useState(false);
//
//     return (
//         <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gray-200">
//             <div className="px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
//                 {/* Brand + Nav */}
//                 <div className="flex items-center gap-6">
//                     <a href="/" className="flex items-center gap-2">
//                         <Zap className="h-7 w-7 text-indigo-600" />
//                         <span className="text-lg font-semibold tracking-tight">EnergyChain</span>
//                     </a>
//                     <nav className="hidden md:flex items-center gap-4 text-sm">
//                         <a href="/dashboard" className="text-gray-600 hover:text-gray-900">Dashboard</a>
//                         <a href="/energy-marketplace" className="text-indigo-700 font-medium">Marketplace</a>
//                         {/*<a href="/analytics" className="text-gray-600 hover:text-gray-900">Analytics</a>*/}
//                         {/*<a href="/companies" className="text-gray-600 hover:text-gray-900">Companies</a>*/}
//                     </nav>
//                 </div>
//
//                 {/* Actions */}
//                 <div className="flex items-center gap-2">
//                     <a
//                         href="#search"
//                         className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-200 hover:bg-gray-50 text-gray-700"
//                         onClick={(e) => {
//                             const el = document.getElementById("global-search");
//                             if (el) { e.preventDefault(); el.focus(); }
//                         }}
//                     >
//                         <Search className="h-4 w-4" />
//                         Search
//                     </a>
//
//                     <a
//                         href="/login"
//                         onClick={(e) => {
//                             if (onLogin) { e.preventDefault(); onLogin(); }
//                         }}
//                         className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
//                     >
//                         Login
//                     </a>
//
//                     <button
//                         className="md:hidden inline-flex items-center justify-center p-2 rounded-md hover:bg-gray-100"
//                         onClick={() => setOpen((s) => !s)}
//                         aria-label="Open menu"
//                     >
//                         <Menu className="h-5 w-5" />
//                     </button>
//                 </div>
//             </div>
//
//             {/* Mobile menu */}
//             {open && (
//                 <div className="md:hidden border-t border-gray-200">
//                     <div className="px-4 py-3 space-y-2 text-sm">
//                         <a href="/" className="block text-gray-700">Dashboard</a>
//                         <a href="/marketplace" className="block text-indigo-700 font-medium">Marketplace</a>
//                         <a href="/analytics" className="block text-gray-700">Analytics</a>
//                         <a href="/companies" className="block text-gray-700">Companies</a>
//                         <a href="/login" className="block text-gray-700">Login</a>
//                     </div>
//                 </div>
//             )}
//         </header>
//     );
// }

// // ---------- Footer ----------
// function Footer() {
//     return (
//         <footer className="border-t border-gray-200 bg-white">
//             <div className="px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
//                 <div>
//                     <div className="flex items-center gap-2 mb-2">
//                         <Zap className="h-5 w-5 text-indigo-600" />
//                         <span className="font-semibold">EnergyChain</span>
//                     </div>
//                     <p className="text-gray-500">
//                         Market intelligence for energy production—compare output, plans, and capacity at a glance.
//                     </p>
//                 </div>
//                 <div>
//                     <div className="font-medium text-gray-900 mb-2">Product</div>
//                     <ul className="space-y-1">
//                         <li><a href="/marketplace" className="text-gray-600 hover:text-gray-900">Marketplace</a></li>
//                         <li><a href="/analytics" className="text-gray-600 hover:text-gray-900">Analytics</a></li>
//                         <li><a href="/companies" className="text-gray-600 hover:text-gray-900">Companies</a></li>
//                     </ul>
//                 </div>
//                 <div>
//                     <div className="font-medium text-gray-900 mb-2">Resources</div>
//                     <ul className="space-y-1">
//                         <li><a href="/docs" className="text-gray-600 hover:text-gray-900">Docs</a></li>
//                         <li><a href="/support" className="text-gray-600 hover:text-gray-900">Support</a></li>
//                         <li><a href="/privacy" className="text-gray-600 hover:text-gray-900">Privacy</a></li>
//                     </ul>
//                 </div>
//             </div>
//             <div className="px-4 sm:px-6 lg:px-8 py-4 text-xs text-gray-500 border-t">
//                 © {new Date().getFullYear()} EnergyChain. All rights reserved.
//             </div>
//         </footer>
//     );
// }

// ---------- Tiny helpers ----------
const Badge = ({ children, tone = "indigo" }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-${tone}-100 text-${tone}-800`}>
    {children}
  </span>
);

const RatingStars = ({ value }) => {
    const full = Math.floor(value);
    const half = value - full >= 0.5;
    const empties = 5 - full - (half ? 1 : 0);
    return (
        <div className="flex items-center gap-0.5">
            {Array.from({ length: full }).map((_, i) => (
                <Star key={`f${i}`} className="h-4 w-4 fill-amber-400 text-amber-400" />
            ))}
            {half && <Star className="h-4 w-4 text-amber-400" />}
            {Array.from({ length: empties }).map((_, i) => (
                <Star key={`e${i}`} className="h-4 w-4 text-gray-300" />
            ))}
            <span className="text-xs text-gray-500 ml-1">{value.toFixed(1)}</span>
        </div>
    );
};

const Sparkline = ({ data, color = "#10B981" }) => (
    <div className="h-16 w-full">
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
                <XAxis dataKey="w" hide />
                <YAxis hide />
                <Tooltip
                    contentStyle={{ background: "white", border: "none", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)" }}
                    formatter={(v) => [`${v} MWh`, "Week"]}
                />
                <Area type="monotone" dataKey="mwh" stroke={color} fill={color} fillOpacity={0.15} />
            </AreaChart>
        </ResponsiveContainer>
    </div>
);

// ---------- Card ----------
function CompanyCard({ c, onView }) {
    const delta = c.currentProductionMWh - c.previousProductionMWh;
    const deltaSign = delta >= 0 ? "+" : "–";
    const color = c.type === "Producer" ? "#10B981" : "#6366F1";

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col">
            <div className="flex items-start justify-between">
                <div>
                    <h3 className="text-base font-semibold text-gray-900">{c.name}</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                        <Badge tone={c.type === "Producer" ? "green" : "blue"}>{c.type}</Badge>
                        <Badge tone="purple">{c.fuel}</Badge>
                        <span className="inline-flex items-center text-xs text-gray-500">
              <MapPin className="h-3.5 w-3.5 mr-1" />
                            {c.region}
            </span>
                    </div>
                </div>
                <button
                    onClick={() => onView(c)}
                    className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                >
                    <Eye className="h-4 w-4" />
                    View
                </button>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500">Previous</div>
                    <div className="text-sm font-semibold">{c.previousProductionMWh.toLocaleString()} MWh</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500">Current</div>
                    <div className="text-sm font-semibold">{c.currentProductionMWh.toLocaleString()} MWh</div>
                    <div className={`text-[11px] ${delta >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {deltaSign}{Math.abs(delta).toLocaleString()} vs prev
                    </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500">Planned</div>
                    <div className="text-sm font-semibold">{c.plannedProductionMWh.toLocaleString()} MWh</div>
                </div>
            </div>

            <div className="mt-4">
                <Sparkline data={c.weeklyTrend} color={color} />
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3 text-xs text-gray-600">
                <div className="flex items-center gap-1.5">
                    <Gauge className="h-4 w-4 text-gray-500" />
                    Utilization <span className="font-medium text-gray-900 ml-1">{c.utilizationPct}%</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <Factory className="h-4 w-4 text-gray-500" />
                    Capacity <span className="font-medium text-gray-900 ml-1">{c.capacityMW} MW</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <User className="h-4 w-4 text-gray-500" />
                    Price <span className="font-medium text-gray-900 ml-1">${c.pricePerKWh.toFixed(3)}/kWh</span>
                </div>
            </div>

            <div className="mt-3 flex items-center justify-between">
                <RatingStars value={c.rating} />
                <div className="text-[11px] text-gray-400">Updated {c.lastUpdated}</div>
            </div>
        </div>
    );
}

// ---------- Detail Drawer ----------
function DetailDrawer({ company, onClose }) {
    if (!company) return null;
    const planData = company.monthPlan.map((m) => ({ month: m.month, planned: m.planned }));
    const histData = company.weeklyTrend.map((d) => ({ ...d }));

    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="absolute right-0 top-0 h-full w-full sm:w-[540px] bg-white shadow-xl p-6 overflow-y-auto">
                <div className="flex items-start justify-between">
                    <div>
                        <h2 className="text-xl font-semibold">{company.name}</h2>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                            <Badge tone={company.type === "Producer" ? "green" : "blue"}>{company.type}</Badge>
                            <Badge tone="purple">{company.fuel}</Badge>
                            <span className="inline-flex items-center text-xs text-gray-500">
                <MapPin className="h-3.5 w-3.5 mr-1" />
                                {company.region}
              </span>
                            <span className="inline-flex items-center text-xs text-gray-500">
                <CalendarDays className="h-3.5 w-3.5 mr-1" />
                Updated {company.lastUpdated}
              </span>
                        </div>
                    </div>
                    <button className="p-2 rounded-md hover:bg-gray-100" onClick={onClose} aria-label="Close">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500">Previous</div>
                        <div className="text-sm font-semibold">{company.previousProductionMWh.toLocaleString()} MWh</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500">Current</div>
                        <div className="text-sm font-semibold">{company.currentProductionMWh.toLocaleString()} MWh</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500">Planned</div>
                        <div className="text-sm font-semibold">{company.plannedProductionMWh.toLocaleString()} MWh</div>
                    </div>
                </div>

                <div className="mt-6">
                    <h3 className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
                        <Layers className="h-4 w-4" />
                        Plants
                    </h3>
                    {company.plants.length === 0 ? (
                        <div className="text-sm text-gray-500">No plants listed</div>
                    ) : (
                        <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                            {company.plants.map((p, i) => (
                                <li key={i} className="p-3 flex items-center justify-between">
                                    <div>
                                        <div className="text-sm font-medium">{p.name}</div>
                                        <div className="text-xs text-gray-500">{p.location}</div>
                                    </div>
                                    <div className="text-sm font-semibold">{p.mw} MW</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="mt-6 grid grid-cols-1 gap-6">
                    <div>
                        <h3 className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
                            <BatteryCharging className="h-4 w-4" />
                            Monthly Plan
                        </h3>
                        <div className="h-56 bg-white rounded-md border">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={planData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <Tooltip
                                        contentStyle={{ background: "white", border: "none", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,.15)" }}
                                        formatter={(v) => [`${v} MWh`, "Planned"]}
                                    />
                                    <Bar dataKey="planned" fill="#6366F1" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
                            <BatteryCharging className="h-4 w-4" />
                            Weekly History
                        </h3>
                        <div className="h-40 bg-white rounded-md border">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={histData}>
                                    <XAxis dataKey="w" />
                                    <YAxis />
                                    <Tooltip
                                        contentStyle={{ background: "white", border: "none", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,.15)" }}
                                        formatter={(v) => [`${v} MWh`, "Week"]}
                                    />
                                    <Area type="monotone" dataKey="mwh" stroke="#10B981" fill="#10B981" fillOpacity={0.15} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------- Page ----------
export default function EnergyMarketplaceList() {
    const [q, setQ] = useState("");
    const [type, setType] = useState("All");
    const [region, setRegion] = useState("All");
    const [fuel, setFuel] = useState("All");
    const [minRating, setMinRating] = useState(0);
    const [sort, setSort] = useState("Output (desc)");
    const [showFilters, setShowFilters] = useState(true);
    const [selected, setSelected] = useState(null); // for drawer

    const [walletState, setWalletState] = useState({
        account: null,
        walletName: null,
        connected: false,
        provider: null,
        signer: null,
    });

    const regions = ["All", ...Array.from(new Set(companies.map((c) => c.region)))];
    const fuels = ["All", ...Array.from(new Set(companies.map((c) => c.fuel)))];

    const filtered = useMemo(() => {
        let list = companies.slice();
        if (type !== "All") list = list.filter((c) => c.type === type);
        if (region !== "All") list = list.filter((c) => c.region === region);
        if (fuel !== "All") list = list.filter((c) => c.fuel === fuel);
        if (q.trim()) {
            const t = q.trim().toLowerCase();
            list = list.filter(
                (c) =>
                    c.name.toLowerCase().includes(t) ||
                    c.region.toLowerCase().includes(t) ||
                    c.fuel.toLowerCase().includes(t)
            );
        }
        list = list.filter((c) => c.rating >= minRating);
        switch (sort) {
            case "Output (desc)":
                list.sort((a, b) => b.currentProductionMWh - a.currentProductionMWh);
                break;
            case "Utilization (desc)":
                list.sort((a, b) => b.utilizationPct - a.utilizationPct);
                break;
            case "Price (asc)":
                list.sort((a, b) => a.pricePerKWh - b.pricePerKWh);
                break;
            case "Rating (desc)":
                list.sort((a, b) => b.rating - a.rating);
                break;
            default:
                break;
        }
        return list;
    }, [q, type, region, fuel, minRating, sort]);

    // Optional: wire this to your auth flow
    const handleLogin = () => {
        // e.g., navigate("/login") if using react-router
        window.location.href = "/login";
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            
            <PageHeader
                onLogin={handleLogin}
                walletState={walletState}
                onWalletStateChange={setWalletState}
            />
            {/* Header spacer to avoid content jump when sticky */}
            <div className="h-2" />

            {/* Page Intro */}
            <div className="px-4 sm:px-6 lg:px-8 py-6">
                <h1 className="text-2xl font-bold">Energy Production Marketplace</h1>
                <p className="text-gray-600 mt-1">
                    One-glance overview of producers—compare specs, see trends, and drill into details.
                </p>
            </div>

            {/* Filters Bar */}
            <div className="px-4 sm:px-6 lg:px-8 mb-4">
                <div className="bg-white rounded-xl shadow-sm border p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <Filter className="h-4 w-4" />
                            Filters
                        </div>
                        <button
                            className="text-sm text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
                            onClick={() => setShowFilters((s) => !s)}
                        >
                            {showFilters ? <>Hide <ChevronUp className="h-4 w-4" /></> : <>Show <ChevronDown className="h-4 w-4" /></>}
                        </button>
                    </div>

                    {showFilters && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    id="global-search"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search name, region, fuel…"
                                    className="pl-9 pr-3 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <select
                                className="border border-gray-300 rounded-md px-2 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                            >
                                <option>All</option>
                                <option>Producer</option>
                                <option>Consumer</option>
                            </select>

                            <select
                                className="border border-gray-300 rounded-md px-2 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                value={region}
                                onChange={(e) => setRegion(e.target.value)}
                            >
                                {regions.map((r) => <option key={r}>{r}</option>)}
                            </select>

                            <select
                                className="border border-gray-300 rounded-md px-2 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                value={fuel}
                                onChange={(e) => setFuel(e.target.value)}
                            >
                                {fuels.map((f) => <option key={f}>{f}</option>)}
                            </select>

                            <div className="flex items-center gap-2">
                                <Star className="h-4 w-4 text-amber-400" />
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="5"
                                    value={minRating}
                                    onChange={(e) => setMinRating(Number(e.target.value))}
                                    className="w-full border border-gray-300 rounded-md px-2 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Min rating"
                                />
                            </div>

                            <select
                                className="border border-gray-300 rounded-md px-2 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                            >
                                <option>Output (desc)</option>
                                <option>Utilization (desc)</option>
                                <option>Price (asc)</option>
                                <option>Rating (desc)</option>
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* Results count */}
            <div className="px-4 sm:px-6 lg:px-8 mb-2 text-sm text-gray-500">
                Showing <span className="font-medium text-gray-700">{filtered.length}</span> companies
            </div>

            {/* Grid */}
            <div className="px-4 sm:px-6 lg:px-8 pb-12">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filtered.map((c) => <CompanyCard key={c.id} c={c} onView={setSelected} />)}
                </div>
            </div>

            <DetailDrawer company={selected} onClose={() => setSelected(null)} />

            <PageFooter />
        </div>
    );
}
